import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, FileText, ChevronRight, Clock, GitCompareArrows, Lock, LockOpen, Loader2 } from "lucide-react";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { api, errMsg } from "../lib/api";
import { checkSvpFile } from "../lib/bfu";
import { toast } from "sonner";

const STATUS = { queued: "Ve frontě", running: "Probíhá", done: "Dokončeno", failed: "Selhalo", cancelled: "Zrušeno" };

export default function Dashboard() {
  const navigate = useNavigate();
  const inputRef = useRef();
  const [audits, setAudits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [selected, setSelected] = useState([]);

  const toggleCompare = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev.slice(-1), id]);
  };

  const prevActive = useRef(new Set());

  useEffect(() => {
    let timer;
    const load = async () => {
      try {
        const r = await api.get("/audits");
        setAudits(r.data);
        r.data.forEach((a) => {
          if (a.status === "done" && prevActive.current.has(a.id)) {
            prevActive.current.delete(a.id);
            toast.success(`Kontrola dokumentu „${a.document_filename}" je hotová! Klikněte na ni v seznamu níže.`, { duration: 10000 });
          }
        });
        const active = r.data.filter((a) => a.status === "running" || a.status === "queued");
        active.forEach((a) => prevActive.current.add(a.id));
        if (active.length > 0) timer = setTimeout(load, 4000);
      } catch {}
    };
    load();
    return () => clearTimeout(timer);
  }, []);

  const activeAudits = audits.filter((a) => a.status === "running" || a.status === "queued");

  const startAudit = async (documentId) => {
    try {
      const { data } = await api.post("/audits", { document_id: documentId });
      navigate(`/audits/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const [pendingDoc, setPendingDoc] = useState(null);
  const [quota, setQuota] = useState(null);
  const confirmRef = useRef();

  useEffect(() => {
    if (pendingDoc) setTimeout(() => confirmRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  }, [pendingDoc]);

  useEffect(() => {
    api.get("/quota").then((r) => setQuota(r.data)).catch(() => {});
  }, []);

  const uploadFile = async (file) => {
    if (!file) return;
    const check = checkSvpFile(file);
    if (!check.ok) {
      toast.error(check.message, { duration: 9000 });
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/documents", fd);
      toast.success(`Dokument zpracován — ${data.page_count} stran`);
      setPendingDoc(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="stagger">
        {activeAudits.length > 0 && (
          <div className="mb-8 rounded-2xl border-2 border-emerald-500 bg-emerald-50 p-6 shadow-md fade-in" data-testid="background-audit-banner">
            <div className="flex items-center gap-3">
              <Loader2 className="h-8 w-8 shrink-0 animate-spin text-emerald-600" />
              <div>
                <h2 className="text-xl font-extrabold text-slate-900 sm:text-2xl">Kontrola vašeho ŠVP právě probíhá</h2>
                <p className="mt-0.5 text-sm text-slate-600">Nemusíte nic dělat — stránka se aktualizuje sama a po dokončení vás upozorníme.</p>
              </div>
            </div>
            <div className="mt-5 space-y-5">
              {activeAudits.map((a) => {
                const p = a.progress || {};
                const queued = a.status === "queued";
                return (
                  <div key={a.id} data-testid={`background-audit-${a.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-bold text-slate-900">{a.document_filename || "Dokument"}</span>
                      <span className="text-sm font-bold text-emerald-700">{queued ? "Ve frontě" : `${Math.round(p.percent || 0)} %`}</span>
                    </div>
                    <Progress value={queued ? 0 : (p.percent || 0)} className="mt-2 h-3" />
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-medium text-emerald-800">
                        {queued ? "Čeká ve frontě — spustí se automaticky" : `${p.stage || "Probíhá kontrola"}${p.detail ? ` · ${p.detail}` : ""}`}
                      </span>
                      <Button variant="outline" size="sm" className="border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-100"
                        data-testid={`open-running-audit-${a.id}`} onClick={() => navigate(`/audits/${a.id}`)}>
                        Sledovat průběh <ChevronRight className="ml-1 h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div className="max-w-3xl">
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Jak je na tom vaše ŠVP<br />vzhledem k novému RVP PV?
          </h1>
          <p className="mt-3 text-base text-slate-600">
            Nahrajte současný školní vzdělávací program vaší mateřské školy. Audit engine zkontroluje strukturu,
            povinné náležitosti, pokrytí OVU a konzistenci — a ukáže vám konkrétní zjištění s důkazy přímo z vašeho dokumentu.
          </p>
        </div>

        <div
          data-testid="svp-upload-dropzone"
          onClick={() => !busy && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); uploadFile(e.dataTransfer.files?.[0]); }}
          className={`mt-8 cursor-pointer rounded-2xl border-2 border-dashed p-14 text-center transition-all duration-200
            ${drag ? "border-emerald-500 bg-emerald-50" : "border-emerald-300 bg-emerald-50/40 hover:border-emerald-500 hover:bg-emerald-50"}`}
        >
          <input ref={inputRef} type="file" accept=".pdf,.docx" className="hidden" data-testid="svp-upload-input"
            onChange={(e) => uploadFile(e.target.files?.[0])} />
          <UploadCloud className="mx-auto h-12 w-12 text-emerald-600" />
          <p className="mt-3 text-lg font-bold text-slate-900">
            {busy ? "Zpracovávám dokument…" : "Klikněte sem a vyberte soubor se svým ŠVP"}
          </p>
          <p className="mt-1 text-sm text-slate-500">PDF nebo DOCX · max. 50 MB · čeština</p>
        </div>

        {pendingDoc && (
          <div ref={confirmRef} className="mt-6 rounded-2xl border-2 border-emerald-400 bg-emerald-50/70 p-6 fade-in" data-testid="confirm-audit-box">
            <div className="text-base font-bold text-slate-900">{pendingDoc.filename}</div>
            <div className="mt-0.5 text-sm text-slate-600">{pendingDoc.page_count} stran · dokument je připraven. Chcete zahájit kontrolu?</div>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <Button className="h-14 flex-1 bg-emerald-600 text-base font-bold hover:bg-emerald-700 sm:max-w-xs" data-testid="confirm-start-audit-button"
                onClick={() => { const id = pendingDoc.id; setPendingDoc(null); startAudit(id); }}>
                Ano, spustit kontrolu
              </Button>
              <Button variant="outline" className="h-14 text-base sm:px-8" data-testid="cancel-pending-audit-button"
                onClick={() => { setPendingDoc(null); toast.info("Kontrola nebyla zahájena. Dokument můžete nahrát znovu kdykoli."); }}>
                Teď ne
              </Button>
            </div>
          </div>
        )}

        <div className="mt-10">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-bold text-slate-900">Vaše audity</h2>
              {quota?.full_access && (
                <span data-testid="quota-counter"
                  className={`rounded-full px-2.5 py-1 text-xs font-bold ${quota.remaining > 0 ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                  {quota.remaining > 0
                    ? `Zbývá ${quota.remaining} z ${quota.limit} kontrol v ceně`
                    : `Vyčerpáno ${quota.limit} z ${quota.limit} kontrol`}
                </span>
              )}
            </div>
            {selected.length === 2 && (
              <Button size="sm" onClick={() => navigate(`/compare/${selected[0]}/${selected[1]}`)}
                className="bg-slate-900 hover:bg-slate-700" data-testid="compare-audits-button">
                <GitCompareArrows className="mr-1.5 h-3.5 w-3.5" /> Porovnat vybrané audity
              </Button>
            )}
          </div>
          {audits.filter((a) => a.status === "done").length >= 2 && (
            <p className="mt-1 text-xs text-slate-400">Tip: zaškrtněte dva dokončené audity a porovnejte, jak se ŠVP posunulo.</p>
          )}
          {audits.length === 0 ? (
            <p className="mt-2 text-sm text-slate-500" data-testid="no-audits-text">Zatím nemáte žádný audit. Nahrajte ŠVP výše.</p>
          ) : (
            <div className="mt-3 divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white">
              {audits.map((a) => (
                <div key={a.id} className="flex items-center gap-3 px-5 py-4 transition-colors hover:bg-slate-50">
                  {a.status === "done" && (
                    <input type="checkbox" checked={selected.includes(a.id)} onChange={() => toggleCompare(a.id)}
                      data-testid={`compare-checkbox-${a.id}`} className="accent-emerald-600" />
                  )}
                  <button data-testid={`audit-row-${a.id}`} onClick={() => navigate(`/audits/${a.id}`)}
                    className="flex flex-1 items-center justify-between gap-4 text-left min-w-0">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="h-5 w-5 shrink-0 text-slate-400" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="truncate text-sm font-semibold text-slate-900">{a.document_filename || "Dokument"}</span>
                          {a.status === "done" && (a.paid ? (
                            <span className="flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800"><LockOpen className="h-2.5 w-2.5" /> Plná verze</span>
                          ) : (
                            <span className="flex shrink-0 items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500"><Lock className="h-2.5 w-2.5" /> Náhled</span>
                          ))}
                        </div>
                        <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                          <Clock className="h-3 w-3" />
                          {new Date(a.created_at).toLocaleString("cs-CZ")} · {STATUS[a.status] || a.status}
                          {a.summary && ` · ${a.summary.critical_count} kritických · ${a.summary.high_count} závažných`}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
